import { useLocation } from 'react-router-dom';
import { useProjectDetailState } from './useApi/useProjectDetail';
import { useProjectInfoState } from './useApi/useProjectInfo';
import { useProjectElementState } from './useApi/useProjectElement';
import { useProjectListState } from './useApi/useProjectList';
import { useIsPageOwner } from './useIsPageOwner';
import { usePageOwnerIdentityStore } from '../store/pageOwnerIdentityStore';
import {
  canShowProjectDetail,
  canShowProjectElementList,
  canShowProjectInfoList,
} from '../util/visibility/projectDetailVisibility';
import { canShowProjectList } from '../util/visibility/projectListVisibility';
import type { ResourceScope } from '../util/scopedResource';

/**
 * ModalTemplate은 App의 Routes 형제라 nested useParams의 projectId를 못 받음.
 * pathname에서 읽어 모달·페이지 공통으로 사용.
 */
const useRouteProjectId = (): string | undefined => {
  const { pathname } = useLocation();
  return pathname.match(/\/projects\/([^/?#]+)/)?.[1];
};

/** Project 단일 엔티티 canShow (모달·페이지 공통) */
export const useCanShowProjectDetail = (): boolean => {
  const projectId = useRouteProjectId();
  const isOwner = useIsPageOwner();
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const { projectId: cachedProjectId, projectOwnerAui, projectScope, status } =
    useProjectDetailState();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';

  return canShowProjectDetail({
    pageOwnerAui,
    resourceOwnerAui: projectOwnerAui,
    resourceScope: projectScope,
    currentScope,
    status,
    resourceProjectId: cachedProjectId,
    currentProjectId: projectId,
  });
};

export const useCanShowProjectInfoList = (): boolean => {
  const projectId = useRouteProjectId();
  const isOwner = useIsPageOwner();
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const { listOwnerAui, listScope, listProjectId, status } = useProjectInfoState();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';

  return canShowProjectInfoList({
    pageOwnerAui,
    resourceOwnerAui: listOwnerAui,
    resourceScope: listScope,
    currentScope,
    status,
    resourceProjectId: listProjectId,
    currentProjectId: projectId,
  });
};

export const useCanShowProjectElementList = (): boolean => {
  const projectId = useRouteProjectId();
  const isOwner = useIsPageOwner();
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const { listOwnerAui, listScope, listProjectId, status } = useProjectElementState();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';

  return canShowProjectElementList({
    pageOwnerAui,
    resourceOwnerAui: listOwnerAui,
    resourceScope: listScope,
    currentScope,
    status,
    resourceProjectId: listProjectId,
    currentProjectId: projectId,
  });
};

/** Projects 목록 canShow (Indexing 모달 등) */
export const useCanShowProjectList = (): boolean => {
  const isOwner = useIsPageOwner();
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const { listOwnerAui, listScope, status } = useProjectListState();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';

  return canShowProjectList({
    pageOwnerAui,
    listOwnerAui,
    listScope,
    currentScope,
    status,
  });
};
